// pipe: the per-user preference store — one LiveData per key, loaded once per session from
// `GET /api/me/preferences`, saved optimistically through `PUT /api/me/preferences/:key` with the
// held version, rolled back to the server's current row on a stale (409) answer. The transport is
// injected so the store is testable without the framework; `ErpService` supplies the real one.
import { LiveData } from '@toeverything/infra';

export const KEY_NAV_LAYOUT = 'nav.layout.v1';
export const KEY_DASHBOARD_LAYOUT = 'dashboard.layout.v1';

/** One stored row, as the server returns it (camelCase). */
export interface PreferenceRow {
  key: string;
  value: unknown;
  version: number;
  updatedAt: string;
}

/** The server refused a save because another device saved first; `current` is the row now. */
export class PreferenceStaleError extends Error {
  constructor(readonly current: PreferenceRow) {
    super('preference is stale');
    this.name = 'PreferenceStaleError';
  }
}

export interface PreferenceTransport {
  listPreferences(): Promise<PreferenceRow[]>;
  /** `version: null` = create only if absent. Throws `PreferenceStaleError` on 409. */
  putPreference(
    key: string,
    value: unknown,
    version: number | null
  ): Promise<PreferenceRow>;
  deletePreference(key: string): Promise<void>;
}

export interface PreferenceState<T> {
  value: T | null;
  version: number | null;
  /** False until the session's first list has answered (or failed). */
  loaded: boolean;
}

export interface UserPreferenceStore {
  pref<T>(key: string): LiveData<PreferenceState<T>>;
  load(): Promise<void>;
  save<T>(key: string, value: T): Promise<void>;
  reset(key: string): Promise<void>;
}

export function createUserPreferenceStore(
  transport: PreferenceTransport
): UserPreferenceStore {
  const cells = new Map<string, LiveData<PreferenceState<unknown>>>();
  let loading: Promise<void> | null = null;

  const cellOf = (key: string) => {
    let cell$ = cells.get(key);
    if (!cell$) {
      cell$ = new LiveData<PreferenceState<unknown>>({
        value: null,
        version: null,
        loaded: false,
      });
      cells.set(key, cell$);
    }
    return cell$;
  };
  const apply = (row: PreferenceRow) =>
    cellOf(row.key).next({
      value: row.value,
      version: row.version,
      loaded: true,
    });

  return {
    pref<T>(key: string) {
      return cellOf(key) as LiveData<PreferenceState<T>>;
    },
    load() {
      loading ??= transport
        .listPreferences()
        .then(rows => {
          const got = new Set<string>();
          for (const row of rows) {
            apply(row);
            got.add(row.key);
          }
          for (const [key, cell$] of cells) {
            if (!got.has(key)) cell$.next({ ...cell$.value, loaded: true });
          }
        })
        .catch(() => {
          // A failed list leaves every cell at its default but marks it loaded so the UI renders
          // the defaults instead of waiting forever; the next save still carries version null.
          for (const cell$ of cells.values()) {
            cell$.next({ ...cell$.value, loaded: true });
          }
        });
      return loading;
    },
    async save<T>(key: string, value: T) {
      const cell$ = cellOf(key);
      const before = cell$.value;
      cell$.next({ value, version: before.version, loaded: true });
      try {
        apply(await transport.putPreference(key, value, before.version));
      } catch (e) {
        if (e instanceof PreferenceStaleError) apply(e.current);
        else cell$.next(before);
        throw e;
      }
    },
    async reset(key: string) {
      const cell$ = cellOf(key);
      const before = cell$.value;
      cell$.next({ value: null, version: null, loaded: true });
      try {
        await transport.deletePreference(key);
      } catch (e) {
        cell$.next(before);
        throw e;
      }
    },
  };
}
